import addressChangeFormImg from "@/shared/assets/estate_modules/mail_forwarding/address-change-form.png";

export const MAIL_FORWARDING_AUTHORITY_TEMPLATE = `# Setting Up Mail Forwarding for {{deceased_first_name}}

Since you're the one looking after {{deceased_first_name}}'s affairs, you are generally permitted by the USPS to redirect their mail. This guide has everything you need to get it done in one visit to the post office. We've laid it all out so there are no surprises.

We've filled in your details so this guide is ready when you are. You can print it to bring along, or pull it up on your phone.

# Where to Go

You'll need to visit a full-service USPS Post Office in person. It can be any location, not just the one closest to {{deceased_first_name}}'s home.

[Find a post office near you](https://tools.usps.com/find-location.htm)

- **Go:** Visit an official USPS Post Office.
- **Avoid:** Do not use "Approved Shippers" like UPS Stores or neighborhood shipping shops.
- **Action:** Filter for "Post Offices" when using the USPS locator tool.

When you get to the counter, here's what to say:

> "Hi, I'm here to set up mail forwarding for a loved one who passed away. I have my ID and the court papers ready whenever you need them."

**Note:** Most Post Offices do not take appointments for mail forwarding; it is a walk-in service.

Estate mail forwarding must be handled in person at a USPS location because the online system does not support the required document uploads. If the clerk is unsure, remind them that the USPS handbook specifically allows executors to manage this process in person. Allow about an hour for your visit to account for document review and potential wait times.

# What to Bring

- [ ] Your government-issued photo ID
- [ ] The original legal document (Letters Testamentary, Trust, or Small Estate Affidavit).
- [ ] One printed photocopy of that document for the clerk to keep.
- [ ] The original death certificate and one photocopy.

# How to Fill Out the Form

The Post Office uses a specific red-and-white form that their scanners need to read, so you'll just need to grab a fresh one from the counter. The "Preview" below is for reference only. You must obtain and use the original red-and-white physical form provided by the clerk at the Post Office.

At the counter, ask for PS Form 3575 (Change of Address). We have provided the specific information you need for the form fields below. The rest takes just a few minutes.

![PS Form 3575](${addressChangeFormImg})

**Top of the form**

| Field | What to enter |
| --- | --- |
| 1 - Type of move | Check the box for "Individual." If someone with the same last name still lives at the old address, be sure to select "Individual" so their mail doesn't get swept up and sent to you by mistake. |
| 2 - Is this move temporary? | Select "No" (Permanent). This ensures the mail continues to be redirected for the full 12-month period without requiring an end date. Selecting "Temporary" may result in forwarding stopping after 6 months unless an extension is requested. |
| 3 - Start date | You can pick today or any day coming up that works for you. |
| 4 | Leave blank |

**About {{deceased_first_name}}**

| Field | What to enter |
| --- | --- |
| 5A - Last name | {{deceased_last_name}} |
| 5B - First name | {{deceased_first_name}} |
| 6 | Leave blank |

**Forwarding mail FROM ({{deceased_first_name}}'s address)**

| Field | What to enter |
| --- | --- |
| 7A - Street address | {{deceased_address}} |
| 7C - City | {{deceased_city}} |
| 7D - State | {{deceased_state}} |
| 7E - ZIP | {{deceased_zip}} |

**Forwarding mail TO (your address)**

| Field | What to enter |
| --- | --- |
| 8A - Street address | {{forward_address}} |
| 8C - City | {{forward_city}} |
| 8D - State | {{forward_state}} |
| 8E - ZIP | {{forward_zip}} |

**Signature**

| Field | What to enter |
| --- | --- |
| 9 | Sign your legal name in the signature block and present the Letters Testamentary or Small Estate Affidavit to the clerk to provide the necessary verification of your authority. |

Fields 7B and 8B are for Puerto Rico addresses only.

# After You File

- **Expect:** Forwarding to begin in 7 to 10 business days.
- **Note:** Forwarding usually lasts one year, but catalogs or packages may stop after six months.
- **Warning:** USPS does not forward mail from private carriers like FedEx or UPS.
- **Action:** Periodically check the physical mailbox at the deceased's home for flyers or non-forwarded items.

# Special Cases

- **If there is a dispute:** If other heirs or parties contest the mail forwarding, contact a legal professional or Elayne immediately.
- **If you are unsure of your role:** If your legal authority (e.g., as a trustee vs. executor) is unclear, consult your legal advisor before filing.`;
